import * as React from "react";
import { useNavigate } from "react-router-dom";
import EditIcon from '../../utils/media/icons/edit_icon.png';
// import ViewIcon from '../../utils/media/icons/view_icon.png';
import DeleteIcon from '../../utils/media/icons/delete_icon.png';
import { deleteCompany } from "../../api/users";
import Chip from "../../components/Chip";

function CompanyTable({ users, toast }) {
    
    const navigate = useNavigate();
    
    // Keep users sorted by created date (descending). Try common date fields.
    const sortedUsers = React.useMemo(() => {
        if (!Array.isArray(users)) return [];
        const getTime = (u) => {
            if (!u) return 0;
            const fields = ['createdAt', 'created_at', 'createdOn', 'created_on', 'created', 'created_date'];
            for (const f of fields) {
                if (u[f]) {
                    const t = Date.parse(u[f]);
                    if (!isNaN(t)) return t;
                }
            }
            // Fallback: derive timestamp from Mongo ObjectId if present (first 8 chars are timestamp)
            if (u._id && /^[a-fA-F0-9]{24}$/.test(String(u._id))) {
                try {
                    const ts = parseInt(String(u._id).substring(0, 8), 16) * 1000;
                    if (!isNaN(ts)) return ts;
                } catch (e) {
                    // ignore
                }
            }
            return 0;
        };

        // Sort by created time desc; stable tiebreaker by name or username
        return [...users].sort((a, b) => {
            const ta = getTime(a);
            const tb = getTime(b);
            if (tb !== ta) return tb - ta;
            const nameA = (a.name || a.companyName || a.ngoName || a.username || '').toString();
            const nameB = (b.name || b.companyName || b.ngoName || b.username || '').toString();
            return nameA.localeCompare(nameB);
        });
    }, [users]);

    const [selectedRows, setSelectedRows] = React.useState(new Set());

    const toggleSelectAll = () => {
        if (selectedRows.size === sortedUsers.length) {
            setSelectedRows(new Set());
        } else {
            setSelectedRows(new Set(sortedUsers.map(user => user._id)));
        }
    };

    const toggleSelectRow = (id) => {
        const newSelected = new Set(selectedRows);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedRows(newSelected);
    };

    const handleEdit = (id, userType) => {
        if(userType === 'regular') navigate('/user/edit/user', { state: { _id: id }});
        else if(userType === 'ambassador') navigate('/user/edit/ambassador', { state: { _id: id }});
        else if(userType === 'Company') navigate('/user/create/company', { state: { _id: id }});
        else if(userType === 'NGO') navigate('/user/create/ngo', { state: { _id: id }});
    }
    
    const handleDelete = (id) => {
        const con = window.confirm('Are you sure you want to delete this user?');
        con && deleteCompany(id, toast);
    }
    
    if(sortedUsers.length === 0) {
        return (
            <div className="w-full h-96 flex items-center justify-center">
                <h1 className="text-xl text-gray-400">No Company Found!</h1>
            </div>
        );
    }
    
    return (
        <div className="overflow-x-auto mt-6">
            <table className="w-full border-collapse text-base" role="table">
                <thead className="bg-navBtn">
                    <tr className="rounded-md">
                        <th className="p-3 text-left">
                            <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 accent-primary"
                                checked={selectedRows.size === sortedUsers.length}
                                onChange={toggleSelectAll}
                                aria-label="Select all users"
                            />
                        </th>
                        <th className="p-3 text-left">Srl. No.</th>
                        <th className="p-3 text-left">Name</th>
                        <th className="p-3 text-left">Username</th>
                        <th className="p-3 text-left">Email</th>
                        <th className="p-3 text-left">Industry</th>
                        <th className="p-3 text-left">Status</th>
                        <th className="p-3 text-left">Partnership Start Date</th>
                        {/* <th className="p-3 text-left">Types Of Partnership</th> */}
                        <th className="p-3 text-left">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedUsers.map((user, index) => (
                        <tr
                            key={user._id}
                            className="border-b border-navBtn hover:bg-gray-50"
                        >
                            <td className="p-3">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300 accent-primary mt-1"
                                    checked={selectedRows.has(user._id)}
                                    onChange={() => toggleSelectRow(user._id)}
                                    aria-label={`Select ${user.name}`}
                                />
                            </td>
                            <td className="p-3">{index + 1}</td>
                            <td className="p-3">{user.name}</td>
                            <td className="p-3">{user.username}</td>
                            <td className="p-3">{user.email}</td>
                            <td className="p-3">{user.industry ? user.industry : '-'}</td>
                            <td className="p-3">
                                {user.active === undefined || user.active === null ? (
                                    <span className="text-gray-500">-</span>
                                ) : (
                                    <Chip value={user.active === 'true' || user.active === true ? 'Active' : 'Inactive'} color={user.active === 'true' || user.active === true ? 'green' : 'red'} />
                                )}
                            </td>
                            <td className="p-3">{user.partnershipStartDate ? new Date(user.partnershipStartDate).toDateString() : '-'}</td>
                            {/* <td className="p-3">{user.typeOfPartnership ? user.typeOfPartnership : '-'}</td> */}
                            <td className="p-3">
                                <button className="rounded p-1.5 bg-[rgba(0,122,155,0.1)] hover:bg-[rgba(0,122,155,0.2)] min-h-7 mr-1" onClick={() => handleEdit(user._id, user.userType)}>
                                    <img src={EditIcon} alt="edit" />
                                </button>
                                {/* <button className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7" onClick={() => navigate('/user/view/user', { state: { _id: user._id } })}>
                                    <img src={ViewIcon} alt="view" />
                                </button> */}
                                <button className="rounded p-1.5 bg-[rgba(250,22,22,0.1)] hover:bg-[rgba(250,22,22,0.2)] min-h-7 ml-1 px-2" onClick={() => handleDelete(user._id, user.userType)}>
                                    <img src={DeleteIcon} alt="delete" />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default CompanyTable;